import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, TrendingUp, Clock, CheckCircle, FileText, AlertCircle, DollarSign, Receipt, Plus, Building2, FolderPlus } from 'lucide-react';
import apiClient from '@/utils/api';
import { formatCurrency } from '@/utils/format';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [memberStats, paymentStats] = await Promise.all([
        apiClient.get('/reports/members/statistics'),
        apiClient.get('/reports/payments/summary'),
      ]);

      setStats({
        ...memberStats.data,
        ...paymentStats.data,
      });
    } catch (error) {
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout currentPage="dashboard" title="Dashboard Overview">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  const currency = stats?.currency || 'USD';

  return (
    <AdminLayout currentPage="dashboard" title="Dashboard Overview">
      {/* Quick Actions */}
      <h3 className="text-base md:text-lg font-semibold mb-3 text-muted-foreground">Quick Actions</h3>
      <div className="flex flex-wrap gap-2 mb-6 md:mb-8" data-testid="admin-quick-actions">
        {[
          { label: 'Verify Payments', icon: CheckCircle, to: '/admin/payments?tab=pending', testid: 'qa-verify-payments' },
          { label: 'Approvals', icon: Clock, to: '/admin/approvals', testid: 'qa-approvals' },
          { label: 'Add Member', icon: Users, to: '/admin/members', testid: 'qa-add-member' },
          { label: 'Add Provider', icon: Building2, to: '/admin/providers', testid: 'qa-add-provider' },
          { label: 'Add Document', icon: FolderPlus, to: '/admin/documents', testid: 'qa-add-document' },
          { label: 'Reports', icon: FileText, to: '/admin/reports', testid: 'qa-reports' },
        ].map((a) => {
          const Icon = a.icon;
          return (
            <button
              key={a.label}
              onClick={() => navigate(a.to)}
              data-testid={a.testid}
              className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm font-medium hover:bg-muted transition-colors"
            >
              <Icon className="w-4 h-4 text-primary" />
              {a.label}
            </button>
          );
        })}
      </div>

      {/* Member Statistics */}
      <h3 className="text-base md:text-lg font-semibold mb-4 text-muted-foreground">Member Statistics</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card>
            <CardHeader className="pb-2 p-3 md:p-6 md:pb-2">
              <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">Total Members</CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                <span className="text-2xl md:text-3xl font-bold" data-testid="total-members">{stats?.total_members || 0}</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card>
            <CardHeader className="pb-2 p-3 md:p-6 md:pb-2">
              <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">Active Members</CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-500" />
                <span className="text-2xl md:text-3xl font-bold" data-testid="active-members">{stats?.active_members || 0}</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card>
            <CardHeader className="pb-2 p-3 md:p-6 md:pb-2">
              <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">Pending Approvals</CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 md:w-5 md:h-5 text-yellow-500" />
                <span className="text-2xl md:text-3xl font-bold" data-testid="pending-approvals">{stats?.pending_applications || 0}</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <Card className="bg-green-50 border-green-200">
            <CardHeader className="pb-2 p-3 md:p-6 md:pb-2">
              <CardTitle className="text-xs md:text-sm font-medium text-green-700">Total Revenue</CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                <span className="text-lg md:text-2xl font-bold text-green-700" data-testid="total-revenue">
                  {formatCurrency(stats?.total_revenue || 0, currency)}
                </span>
              </div>
              <p className="text-xs text-green-600 mt-1 hidden md:block">From verified payments</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Payment & Invoice Statistics */}
      <h3 className="text-base md:text-lg font-semibold mb-4 text-muted-foreground">Payment & Invoice Statistics</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader className="pb-2 p-3 md:p-6 md:pb-2">
              <CardTitle className="text-xs md:text-sm font-medium text-blue-700">Total Invoices</CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                <span className="text-2xl md:text-3xl font-bold text-blue-700" data-testid="total-invoices">{stats?.total_invoices || 0}</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
          <Card className="bg-emerald-50 border-emerald-200">
            <CardHeader className="pb-2 p-3 md:p-6 md:pb-2">
              <CardTitle className="text-xs md:text-sm font-medium text-emerald-700">Paid Invoices</CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
              <div className="flex items-center gap-2">
                <Receipt className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />
                <span className="text-2xl md:text-3xl font-bold text-emerald-700" data-testid="paid-invoices">{stats?.paid_invoices_count || 0}</span>
              </div>
              <p className="text-xs md:text-sm font-semibold text-emerald-600 mt-1" data-testid="paid-invoices-amount">
                {formatCurrency(stats?.total_paid_invoices_amount || 0, currency)}
              </p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
          <Card className="bg-amber-50 border-amber-200">
            <CardHeader className="pb-2 p-3 md:p-6 md:pb-2">
              <CardTitle className="text-xs md:text-sm font-medium text-amber-700">Unpaid Invoices</CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 md:w-5 md:h-5 text-amber-600" />
                <span className="text-2xl md:text-3xl font-bold text-amber-700" data-testid="unpaid-invoices">{stats?.unpaid_invoices_count || 0}</span>
              </div>
              <p className="text-xs md:text-sm font-semibold text-amber-600 mt-1" data-testid="unpaid-invoices-amount">
                {formatCurrency(stats?.total_unpaid_invoices_amount || 0, currency)}
              </p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}>
          <Card className="bg-red-50 border-red-200">
            <CardHeader className="pb-2 p-3 md:p-6 md:pb-2">
              <CardTitle className="text-xs md:text-sm font-medium text-red-700">Overdue Invoices</CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-red-600" />
                <span className="text-2xl md:text-3xl font-bold text-red-700" data-testid="overdue-invoices">{stats?.overdue_invoices_count || 0}</span>
              </div>
              <p className="text-xs md:text-sm font-semibold text-red-600 mt-1" data-testid="overdue-amount">
                {formatCurrency(stats?.total_overdue_amount || 0, currency)}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Pending Verification */}
      {stats?.pending_verification > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}>
          <Card 
            className="bg-orange-50 border-orange-200 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/admin/payments?tab=pending')}
            data-testid="pending-verification-card"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock className="w-6 h-6 text-orange-600" />
                  <div>
                    <p className="font-semibold text-orange-700">Payments Pending Verification</p>
                    <p className="text-sm text-orange-600">Click to review and verify these payments</p>
                  </div>
                </div>
                <span className="text-3xl font-bold text-orange-700" data-testid="pending-verification">
                  {stats?.pending_verification}
                </span>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard;
